import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { choiceValues, fieldChecked, fieldValue, useModalOpen } from "../../app/components/polaris";
import type {
    StorefrontCuratedGroup,
    StorefrontRenderingMode,
    StorefrontResultFilterMode,
    StorefrontResultsGroupingMode,
    StorefrontResultsSortMode,
} from "../../app/models/StorefrontResultsSettings";
import { STOREFRONT_COMPONENTS } from "../../app/terminology/components";
import { getAvailableSearchModeOptions } from "../../app/terminology/searchModes";
import { useLegacyMode } from "../legacy/LegacyModeContext";
import PlanFeatureStatus from "../plan/PlanFeatureStatus";
import SettingsPageLayout from "../settings/SettingsPageLayout";
import ResultsFilterSettings from "./ResultsFilterSettings";
import {
    type ResultsPageLocationMode,
    type SearchDestinationMode,
    collectionLabel,
    createBlankGroup,
    getEffectiveUiSettings,
    getResultsPageLocationMode,
    getSearchDestinationMode,
    getShopifyFilteringMethod,
    withUiDefaults,
} from "./storefrontSettingsModel";
import useStorefrontSettings from "./useStorefrontSettings";

const groupingOptions = [
    { label: "No grouping", value: "None" },
    { label: "Product type", value: "ProductType" },
    { label: "Collection", value: "Collection" },
    { label: "Custom groups", value: "CuratedGroups" },
];
const resultFilterModeOptions = [
    { label: "Show all", value: "All" },
    { label: "Only selected", value: "Include" },
    { label: "Hide selected", value: "Exclude" },
];
const resultsSortOptions = [
    { label: "Title A-Z", value: "title-asc" },
    { label: "Title Z-A", value: "title-desc" },
    { label: "Price low to high", value: "price-asc" },
    { label: "Price high to low", value: "price-desc" },
    { label: "Exact fit first", value: "exact-fit-first" },
];

const matches = (term: string, ...values: string[]) => values.some(value => value.toLowerCase().includes(term.trim().toLowerCase()));

/** A filterable checkbox list inside a scroll box, for product types and collections. */
function CheckboxPicker<T extends string | number>({ label, items, selected, onToggle }: {
    label: string;
    items: { value: T; label: string; search?: string }[];
    selected: Set<T>;
    onToggle: (value: T, checked: boolean) => void;
}) {
    const [term, setTerm] = useState("");
    const shown = term ? items.filter(item => matches(term, item.label, item.search ?? "")) : items;
    return (
        <s-stack gap="small-200">
            <s-search-field label={label} labelAccessibilityVisibility="exclusive" placeholder={label} value={term}
                onInput={event => setTerm(fieldValue(event))} />
            <s-box border="base" borderRadius="base" padding="small-200">
                <s-scroll-box maxBlockSize="260px">
                    <s-stack gap="small-200">
                        {shown.length === 0 ? <s-text color="subdued">No matches</s-text> : shown.map(item =>
                            <s-checkbox key={item.value} label={item.label} checked={selected.has(item.value)}
                                onChange={event => onToggle(item.value, fieldChecked(event))} />)}
                    </s-stack>
                </s-scroll-box>
            </s-box>
            <s-text color="subdued">{`${selected.size} selected`}</s-text>
        </s-stack>
    );
}

function SearchResultsSettingsPage() {
    const navigate = useNavigate();
    const storefront = useStorefrontSettings();
    const { isLegacy } = useLegacyMode();
    const { themeDraft, resultsDraft, saving, sortedGroups, updateThemeDraft, updateResultsDraft, updateUiSettingsDraft } = storefront;
    const [groupDraft, setGroupDraft] = useState<StorefrontCuratedGroup>();
    const [editingGroupIndex, setEditingGroupIndex] = useState<number>();
    const closeGroupModal = useCallback(() => { setGroupDraft(undefined); setEditingGroupIndex(undefined); }, []);
    const groupModalRef = useModalOpen(!!groupDraft, closeGroupModal);
    const collectionMap = useMemo(
        () => new Map((resultsDraft?.availableCollections ?? []).map(collection => [collection.id, collection])),
        [resultsDraft?.availableCollections]
    );

    if (storefront.loading || !themeDraft || !resultsDraft) return storefront.error
        ? <SettingsPageLayout error={storefront.error}>{null}</SettingsPageLayout>
        : <SettingsPageLayout loading>{null}</SettingsPageLayout>;

    const busy = saving;
    const ui = getEffectiveUiSettings(withUiDefaults(resultsDraft.uiSettings));
    const mode = resultsDraft.renderingMode ?? "CollectionFiltering";
    const appProxyResults = mode === "AppProxyResults";
    const renderedResults = mode === "RenderedResults";
    const collectionFiltering = mode === "CollectionFiltering";
    const storefrontFiltering = getShopifyFilteringMethod(themeDraft) === "StorefrontFiltering";
    const resultsPageLocation = getResultsPageLocationMode(themeDraft);
    const openText = (group: string, query?: string) =>
        navigate(`/settings/storefront-text?${new URLSearchParams({ group, ...(query ? { q: query } : {}) })}`);
    const collectionOptions = resultsDraft.availableCollections.map(collection =>
        ({ value: collection.id, label: collectionLabel(collection), search: collection.handle }));
    const searchModeOptions = getAvailableSearchModeOptions(isLegacy);
    const toggleIn = <T,>(values: T[], value: T, checked: boolean) =>
        checked ? Array.from(new Set([...values, value])) : values.filter(item => item !== value);

    const saveGroup = () => {
        if (!groupDraft?.name.trim()) return;
        const group = {
            ...groupDraft,
            name: groupDraft.name.trim(),
            displayOrder: Number(groupDraft.displayOrder) || resultsDraft.curatedGroups.length + 1,
            collectionIds: Array.from(new Set(groupDraft.collectionIds)),
        };
        const groups = [...resultsDraft.curatedGroups];
        if (editingGroupIndex === undefined) groups.push(group);
        else groups[editingGroupIndex] = group;
        updateResultsDraft({ curatedGroups: groups });
        closeGroupModal();
    };

    return (
        <SettingsPageLayout error={storefront.error} form={storefront.form}>
            {storefront.tagSync.element}

            <s-section heading="Where results appear">
                <s-stack gap="base">
                    {searchModeOptions.length === 1
                        ? <s-stack gap="small-200">
                            <s-text type="strong">{searchModeOptions[0].label}</s-text>
                            <s-text color="subdued">{searchModeOptions[0].description}</s-text>
                        </s-stack>
                        : <s-choice-list label="Where results appear" labelAccessibilityVisibility="exclusive" values={[mode]} disabled={busy}
                            onChange={event => storefront.updateRenderingMode(choiceValues(event)[0] as StorefrontRenderingMode)}>
                            {searchModeOptions.map(option => <s-choice key={option.value} value={option.value}>{option.label}</s-choice>)}
                        </s-choice-list>}
                    <PlanFeatureStatus feature={collectionFiltering ? "themeResults" : "partfitsResults"} />
                </s-stack>
            </s-section>

            {collectionFiltering && <s-section heading="Shopify filtering method">
                <s-stack gap="base">
                    <s-choice-list label="Shopify filtering method" labelAccessibilityVisibility="exclusive" disabled={busy}
                        values={[storefrontFiltering ? "StorefrontFiltering" : "LegacyTagUrl"]}
                        onChange={event => updateThemeDraft("collectionTagFilterEnabled", choiceValues(event)[0] === "StorefrontFiltering")}>
                        <s-choice value="StorefrontFiltering">Shopify storefront filtering (Search & Discovery)</s-choice>
                        <s-choice value="LegacyTagUrl">Legacy collection tag URL</s-choice>
                    </s-choice-list>
                    {storefrontFiltering && <s-banner tone="info">
                        Add the Tags filter in Shopify Search & Discovery.{" "}
                        <s-link href="https://help.shopify.com/en/manual/online-store/storefront-search/search-and-discovery-filters" target="_blank">Filtering setup and limits</s-link>
                    </s-banner>}
                    <s-checkbox label="Show a notice when vehicle filtering isn’t applied" checked={ui.showCollectionFilteringNotice !== false}
                        disabled={busy} onChange={event => updateUiSettingsDraft({ showCollectionFilteringNotice: fieldChecked(event) })} />
                    {!isLegacy && <s-stack direction="inline">
                        <s-link onClick={() => openText("Vehicle Search", "Collection filtering notice")}>Edit notice text</s-link>
                    </s-stack>}
                </s-stack>
            </s-section>}

            <s-section heading="Search behavior">
                <s-stack gap="base">
                    <s-select label="Start the search" value={themeDraft.automaticSearch ? "Automatic" : "Manual"} disabled={busy}
                        onChange={event => updateThemeDraft("automaticSearch", fieldValue(event) === "Automatic")}>
                        <s-option value="Manual">When the customer clicks Search</s-option>
                        <s-option value="Automatic">After all required fields are selected</s-option>
                    </s-select>
                    {!appProxyResults && <s-select label="Search destination" value={getSearchDestinationMode(themeDraft)} disabled={busy}
                        onChange={event => storefront.updateSearchDestinationMode(fieldValue(event) as SearchDestinationMode)}>
                        <s-option value="CurrentCollection">Use current collection when available</s-option>
                        <s-option value="ResultsPage">Always use results page</s-option>
                    </s-select>}
                    <s-checkbox label="Save selected fitment with cart items" checked={ui.saveFitmentToCart === true} disabled={busy}
                        onChange={event => updateUiSettingsDraft({ saveFitmentToCart: fieldChecked(event) })} />
                    {ui.saveFitmentToCart && <s-text-field label="Cart item label" value={ui.cartFitmentLabel ?? ""} maxLength={120}
                        placeholder={resultsDraft.defaultCartFitmentLabel || "Use catalog name"} autocomplete="off" disabled={busy}
                        onInput={event => updateUiSettingsDraft({ cartFitmentLabel: fieldValue(event) })} />}
                    <s-checkbox label="Keep collection links filtered" checked={storefront.persistentFilteringChecked} disabled={busy}
                        onChange={event => storefront.setPersistentFilteringChecked(fieldChecked(event))} />
                    {(appProxyResults || renderedResults) && <s-checkbox label="Limit vehicle search to the current collection"
                        checked={ui.limitVehicleSearchToCurrentCollection === true} disabled={busy}
                        onChange={event => updateUiSettingsDraft({ limitVehicleSearchToCurrentCollection: fieldChecked(event) })} />}
                    {collectionFiltering && <s-checkbox label="Keep Shopify fitment tags aligned with PartFits"
                        checked={ui.keepShopifyFitmentTagsAligned === true} disabled={busy || storefront.tagSync.running}
                        onChange={event => updateUiSettingsDraft({ keepShopifyFitmentTagsAligned: fieldChecked(event) })} />}
                    {collectionFiltering && <s-checkbox label="Filter Shopify keyword searches by vehicle"
                        checked={ui.filterShopifyKeywordSearch === true} disabled={busy}
                        onChange={event => updateUiSettingsDraft({ filterShopifyKeywordSearch: fieldChecked(event) })} />}
                    <s-checkbox label="Let customers type to find a vehicle" checked={!!themeDraft.searchableDropdownsEnabled} disabled={busy}
                        onChange={event => updateThemeDraft("searchableDropdownsEnabled", fieldChecked(event))} />
                    <s-checkbox label="Automatically open the next dropdown" checked={!!themeDraft.autoOpenNextDropdown} disabled={busy}
                        onChange={event => updateThemeDraft("autoOpenNextDropdown", fieldChecked(event))} />
                </s-stack>
            </s-section>

            <s-section heading={appProxyResults ? "Result organization" : "Results page"}>
                <s-stack gap="base">
                    {!appProxyResults && <>
                        <s-select label={renderedResults ? "Results page location" : "Fallback results page"} value={resultsPageLocation} disabled={busy}
                            onChange={event => storefront.updateResultsPageLocationMode(fieldValue(event) as ResultsPageLocationMode)}>
                            <s-option value="Default">Default all-products results page</s-option>
                            <s-option value="Custom">Custom path</s-option>
                        </s-select>
                        {resultsPageLocation === "Custom" && <s-text-field label={renderedResults ? "App results page path" : "Custom results path"}
                            autocomplete="off" value={themeDraft.redirectPath || ""} disabled={busy}
                            placeholder={renderedResults ? "/pages/partfinder-results" : "/collections/all"}
                            details={renderedResults ? `The Shopify page with the ${STOREFRONT_COMPONENTS.rendered_results.label} block` : undefined}
                            onInput={event => updateThemeDraft("redirectPath", fieldValue(event))} />}
                    </>}
                    {(appProxyResults || renderedResults) && <>
                        {appProxyResults && <s-select label="Sort products" value={ui.resultsSortBy} disabled={busy}
                            onChange={event => updateUiSettingsDraft({ resultsSortBy: fieldValue(event) as StorefrontResultsSortMode })}>
                            {resultsSortOptions.map(option => <s-option key={option.value} value={option.value}>{option.label}</s-option>)}
                        </s-select>}
                        <s-select label="Group results by" value={resultsDraft.groupResultsBy ?? "None"} disabled={busy}
                            onChange={event => updateResultsDraft({ groupResultsBy: fieldValue(event) as StorefrontResultsGroupingMode })}>
                            {groupingOptions.map(option => <s-option key={option.value} value={option.value}>{option.label}</s-option>)}
                        </s-select>
                        {resultsDraft.groupResultsBy === "CuratedGroups" && <PlanFeatureStatus feature="customGroups" />}
                        {resultsDraft.groupResultsBy === "ProductType" && <>
                            <s-select label="Product type groups" value={resultsDraft.productTypeFilterMode ?? "All"} disabled={busy}
                                onChange={event => updateResultsDraft({ productTypeFilterMode: fieldValue(event) as StorefrontResultFilterMode })}>
                                {resultFilterModeOptions.map(option => <s-option key={option.value} value={option.value}>{option.label}</s-option>)}
                            </s-select>
                            {resultsDraft.productTypeFilterMode !== "All" && <CheckboxPicker label="Filter product types"
                                items={resultsDraft.availableProductTypes.map(type => ({ value: type, label: type }))}
                                selected={new Set(resultsDraft.productTypeFilterValues)}
                                onToggle={(type, checked) => updateResultsDraft({
                                    productTypeFilterValues: toggleIn(resultsDraft.productTypeFilterValues, type, checked).sort((a, b) => a.localeCompare(b)),
                                })} />}
                        </>}
                        {resultsDraft.groupResultsBy === "Collection" && <>
                            <s-select label="Collection groups" value={resultsDraft.collectionFilterMode ?? "All"} disabled={busy}
                                onChange={event => updateResultsDraft({ collectionFilterMode: fieldValue(event) as StorefrontResultFilterMode })}>
                                {resultFilterModeOptions.map(option => <s-option key={option.value} value={option.value}>{option.label}</s-option>)}
                            </s-select>
                            {resultsDraft.collectionFilterMode !== "All" && <CheckboxPicker label="Filter collections" items={collectionOptions}
                                selected={new Set(resultsDraft.collectionFilterIds)}
                                onToggle={(id, checked) => updateResultsDraft({
                                    collectionFilterIds: toggleIn(resultsDraft.collectionFilterIds, id, checked).sort((a, b) => a - b),
                                })} />}
                        </>}
                        <s-stack direction="inline">
                            <s-link onClick={() => navigate("/settings/appearance")}>Edit product card appearance</s-link>
                        </s-stack>
                    </>}
                </s-stack>
            </s-section>

            {(appProxyResults || renderedResults) && resultsDraft.groupResultsBy === "CuratedGroups" && <s-section heading="Custom result groups" padding={sortedGroups.length ? "none" : "base"}>
                {sortedGroups.length === 0 ? <s-stack gap="base">
                    <s-text color="subdued">No custom groups yet</s-text>
                    <s-stack direction="inline">
                        <s-button icon="plus" disabled={busy} onClick={() => setGroupDraft(createBlankGroup(resultsDraft.curatedGroups.length + 1))}>Add group</s-button>
                    </s-stack>
                </s-stack> : <>
                    <s-table>
                        <s-table-header-row>
                            <s-table-header listSlot="primary">Group</s-table-header>
                            <s-table-header>Shown</s-table-header>
                            <s-table-header>Collections</s-table-header>
                            <s-table-header format="numeric">Order</s-table-header>
                            <s-table-header><s-text accessibilityVisibility="exclusive">Actions</s-text></s-table-header>
                        </s-table-header-row>
                        <s-table-body>
                            {sortedGroups.map(({ group, originalIndex }) => (
                                <s-table-row key={`${group.id ?? "new"}-${originalIndex}`}>
                                    <s-table-cell><s-text fontWeight="semibold">{group.name}</s-text></s-table-cell>
                                    <s-table-cell>{group.include ? "Yes" : "Hidden"}</s-table-cell>
                                    <s-table-cell>{group.collectionIds.map(id => collectionMap.get(id)?.title).filter(Boolean).join(", ") || "None"}</s-table-cell>
                                    <s-table-cell>{group.displayOrder}</s-table-cell>
                                    <s-table-cell>
                                        <s-button-group>
                                            <s-button slot="secondary-actions" variant="tertiary" accessibilityLabel={`Edit ${group.name}`} disabled={busy}
                                                onClick={() => { setEditingGroupIndex(originalIndex); setGroupDraft({ ...group, collectionIds: [...group.collectionIds] }); }}>Edit</s-button>
                                            <s-button slot="secondary-actions" variant="tertiary" tone="critical" accessibilityLabel={`Delete ${group.name}`} disabled={busy}
                                                onClick={() => updateResultsDraft({ curatedGroups: resultsDraft.curatedGroups.filter((_, index) => index !== originalIndex) })}>Delete</s-button>
                                        </s-button-group>
                                    </s-table-cell>
                                </s-table-row>
                            ))}
                        </s-table-body>
                    </s-table>
                    <s-box padding="base">
                        <s-button icon="plus" disabled={busy} onClick={() => setGroupDraft(createBlankGroup(resultsDraft.curatedGroups.length + 1))}>Add group</s-button>
                    </s-box>
                </>}
            </s-section>}

            {appProxyResults && <ResultsFilterSettings ui={ui} pricesHidden={resultsDraft.priceDisplayMode === "HidePrices"} disabled={busy}
                update={updateUiSettingsDraft} onConnectAttribute={() => navigate("/settings/data")} />}

            <s-modal ref={groupModalRef} id="pf-curated-group" heading={editingGroupIndex === undefined ? "Add group" : "Edit group"}>
                {/* s-modal keeps its children mounted; render the form only while open. */}
                {groupDraft && <s-stack gap="base">
                    <s-text-field label="Group name" autocomplete="off" value={groupDraft.name} placeholder="Lighting"
                        onInput={event => setGroupDraft({ ...groupDraft, name: fieldValue(event) })} />
                    <s-number-field label="Display order" min={1} value={String(groupDraft.displayOrder || "")}
                        onInput={event => setGroupDraft({ ...groupDraft, displayOrder: Number(fieldValue(event)) || 0 })} />
                    <s-checkbox label="Show this group in results" checked={groupDraft.include}
                        onChange={event => setGroupDraft({ ...groupDraft, include: fieldChecked(event) })} />
                    <CheckboxPicker label="Filter collections" items={collectionOptions} selected={new Set(groupDraft.collectionIds)}
                        onToggle={(id, checked) => setGroupDraft({ ...groupDraft, collectionIds: toggleIn(groupDraft.collectionIds, id, checked) })} />
                </s-stack>}
                <s-button slot="primary-action" variant="primary" disabled={!groupDraft?.name.trim()} onClick={saveGroup}>
                    {editingGroupIndex === undefined ? "Add group" : "Done"}
                </s-button>
                <s-button slot="secondary-actions" onClick={closeGroupModal}>Cancel</s-button>
            </s-modal>
        </SettingsPageLayout>
    );
}

export default observer(SearchResultsSettingsPage);
